import "./AIPage.scss";
import { ContentAreaType } from "/Models/Generated/ContentAreaType";
import {
  ContentArea,
  GridRow,
  GridPresetMainContent,
  OptimizelyEditProperty,
  AiInfoCardBlock,
  GridCell,
  MinusCircleIcon,
  GridPresetList,
  AITextInput,
  PlusCircleIcon,
  AiInfoBoxBlock,
  HideFor,
  MinusIcon,
  PlusIcon,
} from "/App.Components";
import { AIPageProps } from "/Models/Generated/AIPageProps";
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { useEffect, useRef, useState } from "react";
import { LinkProps } from "/Models/Generated/LinkProps";
import {
  GoogleReCaptchaProvider,
  GoogleReCaptcha,
} from "react-google-recaptcha-v3";
import AiQuestion, {
  AiQuestionProps,
} from "../../Shared/AiQuestion/AiQuestion";
import { randomString } from "/Helpers/RandomString";
import { BackgroundColor } from "/Components/Blocks/AiInfoCardBlock/AiInfoCardBlock";
import { createSpreadableBem } from "/Helpers/createBem";

enum MessageStatus {
  notStarted = "notStarted",
  inProgress = "inProgress",
  finished = "finished",
  error = "error",
}

interface NoradSourceModel {
  title?: string;
  link?: string;
}

interface ChatMessage {
  role: string;
  message: string;
}

const AIPage = ({
  heading,
  headingPropertyName,
  mainBody,
  searchInstructions,
  signalRNegotiateEndpoint,
  aiInfoCards,
  aiInfoBoxBlock,
  recaptchaKey,
  recaptchaLanguage,
  closeButtonText,
  copyButtonText,
  editQuestionButtonText,
  sendButtonText,
  sourcesHeading,
  feedBackText,
  feedBackSubmittedText,
  inputPlaceholderText,
}: AIPageProps) => {
  const [askQuestion, setAskQuestion] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const connectionRef = useRef<HubConnection | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(true);
  const [firstQuestionSent, setFirstQuestionSent] = useState(false);
  const [questions, setQuestions] = useState<AiQuestionProps[]>([]);
  const [prefilledQuestion, setPrefilledQuestion] = useState("");
  const qIdx = useRef(0);
  const pageRef = useRef<HTMLDivElement>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);

  const updateBottomPadding = () => {
    if (!pageRef.current || !inputAreaRef.current) return;
    pageRef.current.style.paddingBottom =
      inputAreaRef.current.clientHeight + "px";
  };

  useEffect(() => updateBottomPadding());

  useEffect(() => {
    window.addEventListener("resize", updateBottomPadding);
    return () => window.removeEventListener("resize", updateBottomPadding);
  }, []);

  useEffect(() => {
    const connectToSignalR = async () => {
      try {
        const connection = new HubConnectionBuilder()
          .withUrl(signalRNegotiateEndpoint)
          .configureLogging(LogLevel.Information)
          .withAutomaticReconnect()
          .build();

        connection
          .start()
          .then(() => {
            connection.on("MessageToClient", (message) => {
              setQuestions((oldQuestions) => {
                const newQuestions = [...oldQuestions];
                newQuestions[qIdx.current].answer =
                  (newQuestions[qIdx.current].answer ?? "") + (message ?? "");

                return newQuestions;
              });
            });

            connection.on("SetMessageStatus", (status: MessageStatus) => {
              setQuestions((oldQuestions) => {
                const newQuestions = [...oldQuestions];
                newQuestions[qIdx.current].finished =
                  status === MessageStatus.finished;
                return newQuestions;
              });
            });

            connection.on("SetSources", (sources: NoradSourceModel[]) => {
              setQuestions((oldQuestions) => {
                const newQuestions = [...oldQuestions];
                newQuestions[qIdx.current].sources = sources
                  .filter((s) => s.link && s.title)
                  .map<LinkProps>((s) => ({
                    text: s.title,
                    href: s.link,
                    isExternal: false,
                  }));
                return newQuestions;
              });
            });
          })
          .catch(() => {
            setStreamError(true);
          });

        connectionRef.current = connection;
      } catch (_error) {
        setStreamError(true);
      }
    };
    connectToSignalR();

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
    };
  }, []);

  const sendMessage = async () => {
    try {
      if (!streamError && recaptchaToken) {
        const questionList = questions.flatMap((q, idx) =>
          idx < questions.length - 1
            ? [
                {
                  role: "user",
                  message: q.question,
                },
                {
                  role: "assistant",
                  message: q.answer,
                },
              ]
            : [
                {
                  role: "user",
                  message: q.question,
                },
              ]
        ) as ChatMessage[];
        connectionRef.current?.send(
          "QuestionFromClient",
          questionList,
          recaptchaToken,
          recaptchaLanguage
        );
      } else {
        // eslint-disable-next-line no-console
        console.error("Missing reCAPTCHA token or stream error.");
      }
    } catch {}
  };

  useEffect(() => {
    if (askQuestion && recaptchaToken) {
      sendMessage();
      setAskQuestion(false);
    }
  }, [askQuestion, recaptchaToken]);

  useEffect(() => {
    if (refreshReCaptcha) {
      setRefreshReCaptcha(false);
    }
  }, [refreshReCaptcha]);

  const bem = createSpreadableBem("ai-page-norad");
  return (
    <div {...bem("")} ref={pageRef}>
      <GoogleReCaptchaProvider
        reCaptchaKey={recaptchaKey ?? ""}
        language={recaptchaLanguage}
      >
        <GoogleReCaptcha
          onVerify={setRecaptchaToken}
          refreshReCaptcha={refreshReCaptcha}
        />
        <div {...bem("__main-content")}>
          {!firstQuestionSent && (
            <GridRow>
              <GridPresetMainContent>
                <OptimizelyEditProperty propertyName={headingPropertyName}>
                  {heading && <h1 {...bem("__heading")}>{heading}</h1>}
                </OptimizelyEditProperty>
                {mainBody && (
                  <div {...bem("__main-body")}>
                    <ContentArea
                      {...mainBody}
                      contentAreaType={ContentAreaType.HalfWidth}
                    />
                  </div>
                )}
                {(aiInfoCards.length ?? 0) > 0 && (
                  <div {...bem("__info-card-blocks")}>
                    {aiInfoCards.map((item, idx) => (
                      <div key={idx} {...bem("__info-card-block-wrapper")}>
                        <AiInfoCardBlock
                          backgroundColor={BackgroundColor.light}
                          {...item}
                          onClickHandler={() => {
                            return setPrefilledQuestion(item.description ?? "");
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </GridPresetMainContent>
            </GridRow>
          )}
          {questions && (
            <div {...bem("__questions")}>
              {questions.map((q, idx) => (
                <AiQuestion
                  key={q.uid}
                  {...q}
                  editQuestionButtonText={editQuestionButtonText}
                  copyButtonText={copyButtonText}
                  sendEditedQuestion={(question) => {
                    qIdx.current = idx;
                    const newQuestion: AiQuestionProps = {
                      question,
                      answer: "",
                      finished: false,
                      sources: [],
                      uid: randomString(20),
                    };
                    setQuestions(
                      questions.toSpliced(idx, question.length, newQuestion)
                    );
                    setRefreshReCaptcha(true);
                    setAskQuestion(true);
                    setTipsOpen(false);
                  }}
                  sourcesHeading={sourcesHeading}
                  sendButtonText={sendButtonText}
                  closeButtonText={closeButtonText}
                  feedBackText={feedBackText}
                  feedBackSubmittedText={feedBackSubmittedText}
                  recaptchaToken={recaptchaToken}
                  sidebarChildren={<AiInfoBoxBlock {...aiInfoBoxBlock} />}
                  isLast={idx === questions.length - 1}
                />
              ))}
            </div>
          )}

          <div {...bem("__question-input-area")} ref={inputAreaRef}>
            <GridRow>
              <GridPresetList>
                <div {...bem("__tips-button-shadow-mobile")}></div>
                <div {...bem("__tips-button-shadow-mobile-cutoff")}></div>
                <button
                  onClick={() => setTipsOpen((prev) => !prev)}
                  {...bem("__tips-button-mobile")}
                >
                  {tipsOpen && (
                    <>
                      <HideFor desktop desktopMedium desktopLargeUp>
                        <div {...bem("__tips-button-mobile-icon")}>
                          <MinusIcon size="20" thickness="Light" />
                        </div>
                      </HideFor>
                      <HideFor tablet mobile>
                        <MinusCircleIcon size="32" thickness="Light" />
                      </HideFor>
                    </>
                  )}
                  {!tipsOpen && (
                    <>
                      <HideFor desktop desktopMedium desktopLargeUp>
                        <PlusIcon size="20" thickness="Light" />
                      </HideFor>
                      <HideFor tablet mobile>
                        <PlusCircleIcon size="32" thickness="Light" />
                      </HideFor>
                    </>
                  )}
                </button>
                <div>
                  <AITextInput
                    prefilledQuestion={prefilledQuestion}
                    placeholder={
                      inputPlaceholderText || "Skriv spørsmålet ditt"
                    }
                    loading={false}
                    onEnter={(value) => {
                      qIdx.current = questions.length;
                      const newQuestion: AiQuestionProps = {
                        question: value,
                        answer: "",
                        uid: randomString(20),
                      };
                      setQuestions([...questions, newQuestion]);
                      setRefreshReCaptcha(true);
                      setAskQuestion(true);
                      setTipsOpen(false);
                      setFirstQuestionSent(true);
                    }}
                  />
                </div>
                <div
                  {...bem("__tips-container", {
                    "--closed": !tipsOpen,
                  })}
                >
                  <div {...bem("__tips-container-inner")}>
                    <ContentArea
                      {...searchInstructions}
                      contentAreaType={ContentAreaType.FullWidth}
                    />
                  </div>
                </div>
              </GridPresetList>
              <GridCell
                spanColsDesktop={1}
                startColsDesktop={12}
                spanColsTablet={6}
                spanColsMobile={6}
                desktopOnly
              >
                <button onClick={() => setTipsOpen((prev) => !prev)}>
                  {tipsOpen && <MinusCircleIcon size="32" thickness="Light" />}
                  {!tipsOpen && <PlusCircleIcon size="32" thickness="Light" />}
                </button>
              </GridCell>
            </GridRow>
          </div>
        </div>
      </GoogleReCaptchaProvider>
    </div>
  );
};

export default AIPage;
